import React from 'react';

import { Box, Text, IconButton, Link, Icon, HStack, Stack } from '@chakra-ui/react';
import { Close, DirectionsCar, Warning, Report, CheckCircle } from '@m/icons';

import { Button } from './button';

export type InformationCardVariant = 'marketing' | 'warning' | 'alert' | 'success';

export interface InformationCardProps {
  body?: React.ReactNode;
  buttonDisabled?: boolean;
  buttonIcon?: React.ReactNode;
  buttonLabel?: string;
  hideIcon?: boolean;
  icon?: React.ElementType;
  linkHref?: string;
  linkOnClick?: () => void;
  linkRel?: string;
  linkTarget?: string;
  linkText?: string;
  onButtonClick?: () => void;
  onClose?: () => void;
  title?: string;
  variant?: InformationCardVariant;
}

const iconMap: Record<InformationCardVariant, React.ElementType> = {
  marketing: DirectionsCar,
  warning: Warning,
  alert: Report,
  success: CheckCircle,
};

const backgroundColorMap: Record<InformationCardVariant, string> = {
  marketing: 'fill.background.accent',
  warning: 'fill.background.warning',
  alert: 'fill.background.error',
  success: 'fill.background.success',
};

const iconColorMap: Record<InformationCardVariant, string> = {
  marketing: 'fill.icon.accent',
  warning: 'fill.icon.warning',
  alert: 'fill.icon.error',
  success: 'fill.icon.success',
};

export const InformationCard: React.FC<InformationCardProps> = ({
  variant = 'marketing',
  title,
  body,
  linkText,
  linkHref,
  linkOnClick,
  linkRel,
  linkTarget,
  buttonDisabled,
  buttonLabel,
  buttonIcon,
  onButtonClick,
  onClose,
  icon,
  hideIcon = false,
}) => {
  const CardIcon = icon || iconMap[variant];
  const computedLinkRel = linkTarget === '_blank' ? (linkRel ?? 'noopener noreferrer') : linkRel;

  return (
    <Box p={4} bg={backgroundColorMap[variant]} borderRadius="md">
      <HStack gap={4}>
        {!hideIcon && <Icon as={CardIcon} boxSize={5} color={iconColorMap[variant]} />}
        <Stack flex="1">
          {title && <Text variant="MainBodyDemiBold">{title}</Text>}
          {body &&
            (typeof body === 'string' ? <Text variant="SmallBodyRegular">{body}</Text> : body)}
          {linkText && (linkHref || linkOnClick) && (
            <Text variant="ExtraSmallBodyRegular">
              {linkOnClick && !linkHref ? (
                <Text
                  as="button"
                  onClick={linkOnClick}
                  textDecoration="underline"
                  cursor="pointer"
                  bg="transparent"
                  border="none"
                  p={0}
                >
                  {linkText}
                </Text>
              ) : (
                <Link
                  href={linkHref}
                  onClick={linkOnClick}
                  variant="underline"
                  target={linkTarget}
                  rel={computedLinkRel}
                >
                  {linkText}
                </Link>
              )}
            </Text>
          )}
        </Stack>
        {buttonLabel && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={onButtonClick}
            disabled={buttonDisabled}
            data-testid="information-card-button"
          >
            <HStack gap={1}>
              {buttonIcon}
              <span>{buttonLabel}</span>
            </HStack>
          </Button>
        )}
        {onClose && (
          <IconButton
            aria-label="Close card"
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="information-card-close-button"
          >
            <Close />
          </IconButton>
        )}
      </HStack>
    </Box>
  );
};
